import * as React from 'react';
import { Box, Button, CircularProgress, PaletteColor } from '@mui/material';
import './ConfirmationModal.css';
import {
    FaOptionIcon,
    alertDefaultIcon,
    alertInfoIcon,
    alertSuccessIcon,
    alertWarningIcon,
    alertErrorIcon,
} from '@europrocurement/l2d-icons';
import { MessagesTypes } from '@europrocurement/flexy-components/constants/wording/modals';
import { useL2DTheme, usePaletteTint } from '@europrocurement/flexy-components/hooks';

export type ConfirmationModalProps = {
    messages?: MessagesTypes;
    actionOnValidation?: () => void;
    actionOnCancellation?: () => void;
    actionOnAlternative?: () => void;
    color: PaletteColor;
    disabled?: boolean;
    isFullWidth?: boolean;
    isSubmitting?: boolean;
};

const getIconProps = (type: string | undefined, color: PaletteColor) => {
    const colorProp = color.main;

    if (type === 'info') {
        return { ...alertInfoIcon.props, color: colorProp };
    }

    if (type === 'success') {
        return { ...alertSuccessIcon.props, color: colorProp };
    }

    if (type === 'warning') {
        return { ...alertWarningIcon.props, color: colorProp };
    }

    if (type === 'error') {
        return { ...alertErrorIcon.props, color: colorProp };
    }

    return { ...alertDefaultIcon.props, color: colorProp };
};

export const ConfirmationModal: React.FunctionComponent<ConfirmationModalProps> = function ({
    messages,
    actionOnValidation,
    actionOnCancellation,
    actionOnAlternative,
    color,
    disabled,
    isFullWidth = false,
    isSubmitting = false,
}) {
    const { palette } = useL2DTheme();
    const tint = usePaletteTint();
    const mainColor = color || palette.primary;
    const iconProps = getIconProps(messages?.detail?.severity || undefined, mainColor);

    return (
        <Box
            className="confirmation-modal"
            sx={{
                maxWidth: isFullWidth ? '100% !important' : undefined,
                backgroundColor: palette.background.dark,
            }}
        >
            <Box className="body">
                <Box
                    className="icon"
                    sx={{ backgroundColor: tint(mainColor.light) }}
                >
                    <FaOptionIcon {...iconProps} />
                </Box>
                <Box
                    className="question"
                    sx={{
                        color: palette.text.primary,
                    }}
                >
                    {messages?.question || 'Êtes-vous sûr de vouloir valider cette action ?'}
                </Box>
                {messages?.detail ? (
                    <Box
                        className="details"
                        sx={{
                            color: palette.text.primary,
                        }}
                    >
                        {messages.detail.message}
                    </Box>
                ) : null}
            </Box>
            <Box className="actions">
                <Button
                    sx={{
                        color: palette.common.white,
                        backgroundColor: mainColor.main,
                        '&:hover': {
                            backgroundColor: mainColor.main,
                        },
                    }}
                    variant="contained"
                    onClick={actionOnValidation}
                    data-testid="validation-button"
                    disabled={disabled || isSubmitting}
                >
                    {isSubmitting ? (
                        <CircularProgress
                            size={20}
                            sx={{ color: palette.common.white }}
                        />
                    ) : (
                        messages?.validation || 'Valider'
                    )}
                </Button>
                {actionOnCancellation ||
                (!actionOnCancellation && !actionOnAlternative) ||
                messages?.annulation ? (
                    <Button
                        className="cancel-button"
                        variant="outlined"
                        onClick={actionOnCancellation}
                        data-testid="cancel-button"
                        disabled={disabled}
                        sx={{
                            color: palette.text.secondary,
                            borderColor: palette.grey[500],
                            '&:hover': {
                                borderColor: palette.grey[500],
                            },
                        }}
                    >
                        {messages?.annulation || 'Annuler'}
                    </Button>
                ) : null}
                {actionOnAlternative && messages?.alternative ? (
                    <Button
                        className="alternate-button"
                        variant="outlined"
                        onClick={actionOnAlternative}
                        data-testid="alternate-button"
                        disabled={disabled}
                        sx={{
                            color: palette.text.secondary,
                            borderColor: palette.grey[500],
                            '&:hover': {
                                borderColor: palette.grey[500],
                            },
                        }}
                    >
                        {messages.alternative}
                    </Button>
                ) : null}
            </Box>
        </Box>
    );
};
